import { action, computed, makeObservable, observable } from 'mobx';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { loadStripe } from '@stripe/stripe-js';

import { Region } from '@components/common/constants';
import { getApiErrorMessage } from '@components/common/util/util';
import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { stripePublishableKey } from '@config';

dayjs.extend(utc);

interface fromTo {
  startDate: number | undefined;
  endDate: number | undefined;
}

interface ICardToken {
  id?: string;
  card?: {
    last4: string;
    brand: string;
    expiry?: string;
  };
}

interface ISetUpIntentInfo {
  clientSecret: string;
  nextAction: string;
  status: string;
}

type SetupIntentInfo = ISetUpIntentInfo | undefined;

interface SubscriptionResponse {
  planId: string;
  planName: PlanNameType;
  status: 'incomplete' | 'past_due';
  paymentStatus: 'requires_action';
  clientSecret: string;
  paymentMethod: string;
}

export interface IPaymentMethod {
  id: string;
  customerId: string;
  card: {
    brand: string;
    country: string;
    expMonth: number;
    expYear: number;
    last4: string;
  };
}

interface ICardDetails {
  setDefault?: boolean;
  cardExpMonth?: number;
  cardExpYear?: number;
}

interface ISubscriptionSchedule {
  productName: string;
  startDate: number;
}

export interface IBillingStore {
  rootStore: IRootStore;
  billingTenure: fromTo | undefined;
  planName: ITenantInfo['planName'] | '';
  price: number;
  amountDue: number;
  paymentMethods: IPaymentMethod[];
  plansDetails: IPlanDetails[];
  cancelAtPeriodEnd: boolean;
  subscriptionSchedule: ISubscriptionSchedule | null;
  loadingPayments: boolean;
  getPlanName: () => void;
  redirectToBilling: () => Promise<void>;
  updatePaymentMethod: (
    id: string,
    cardDetails: ICardDetails,
    isSuccess?: boolean,
  ) => Promise<void>;
  makeDefaultPayment: (isDefault: boolean) => Promise<void>;
  updateDefaultBillingEmail: (email: string) => void;
  updateSubscription: (stripePlanId: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  getInvoicePreview: (stripePlanId: string) => Promise<void>;
  deletePaymentMethod: (paymentMethod: IPaymentMethod) => Promise<void>;
  getPaymentMethods: () => Promise<void>;
  getSelfServePlans: () => Promise<void>;
  getTenantDetail: () => Promise<void>;
  tenantInfo: ITenantInfo | undefined;
  firstLoad: boolean;
  nextBillingDate: number | undefined;
  currentPlan?: IPlanDetails;
  isFreeTier: boolean;
  reset: () => void;
}

interface IBillingInfoResponse {
  name: ITenantInfo['planName'];
  price: number;
  currentPeriodStartAt: number;
  currentPeriodEndAt: number;
  cancelAtPeriodEnd: boolean;
  metadata: { [index: string]: number | string };
  invoiceInfo: InvoiceInfoResp;
  paymentMethod: ICardToken;
  pendingSetupIntentInfo: SetupIntentInfo;
  emails: string[];
  subscriptionSchedule: ISubscriptionSchedule | null;
}

interface InvoiceInfoResp {
  name: string;
  address: {
    city: string;
    country: string;
    line1: string;
    line2: string;
    postalCode: string;
    state: string;
  };
}

export type PlanNameType = 'Enterprise' | 'Starter' | 'Free Tier' | 'Pro' | 'Open source plan';

interface ITenantInfo {
  planName: PlanNameType;
  planId: string;
  createdAt: string;
  currentPlanStartAt: number;
  contractEventVolume: number;
  invoiceEmails: string[];
  defaultPaymentMethod: IPaymentMethod;
  ownerEmail: string;
}

interface IBillingURL {
  url: string;
}

export interface IPlanDetails {
  id: string;
  productName: string;
  planType: 'Starter' | 'Free Tier';
  stripePriceId: string;
  unitAmount: number;
  eventVolume: number;
  prices: {
    id: string;
    recurringInterval: 'month' | 'year';
    unitAmount: number;
    currency: string;
  }[];
}

const billingCycleStartDate = dayjs().utc().startOf('month').unix();
const billingCycleEndDate = dayjs().add(1, 'month').utc().startOf('month').unix();

export class BillingStore implements IBillingStore {
  @observable public rootStore: IRootStore;

  @observable public planName: ITenantInfo['planName'] | '' = '';

  // used for only starter plan price in plans tab in settings page
  @observable public price = 0;

  @observable public amountDue = 0;

  @observable public paymentMethods: IPaymentMethod[] = [];

  @observable public plansDetails: IPlanDetails[] = [];

  @observable public loadingPayments = false;

  @observable public cancelAtPeriodEnd = false;

  @observable public subscriptionSchedule: ISubscriptionSchedule | null = null;

  @observable tenantInfo: ITenantInfo | undefined;

  @observable billingTenure: fromTo | undefined = {
    startDate: billingCycleStartDate * 1000,
    endDate: billingCycleEndDate * 1000,
  };

  @observable firstLoad = false;

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  // get the plan type, the number of credits and other billing info
  public async getPlanName() {
    const { messagesStore, featuresStore } = this.rootStore;
    if (!featuresStore.has('BILLING_PAGE')) {
      return;
    }

    if (!this.tenantInfo) {
      await this.getTenantDetail();
    }

    await this.getSelfServePlans();

    if (this.isFreeTier) {
      const currentPlanStartsAt = dayjs(this.tenantInfo?.currentPlanStartAt)
        .utc()
        .unix();
      const startDate =
        billingCycleStartDate >= currentPlanStartsAt ? billingCycleStartDate : currentPlanStartsAt;
      this.updateBillingDates(startDate, billingCycleEndDate);
      return;
    }

    try {
      const resp: AxiosResponse<IBillingInfoResponse> = await apiAuthCaller({
        region: Region.US,
      }).get('/billing');

      this.updateBillingInfo(resp.data);
    } catch (error) {
      if (featuresStore.has('BILLING_PAGE')) {
        messagesStore.showErrorMessage('Failed to get billing details');
      }
    }
  }

  @action.bound
  private updateBillingInfo(subscriptionInfo: IBillingInfoResponse) {
    const {
      name,
      currentPeriodStartAt,
      currentPeriodEndAt,
      price,
      cancelAtPeriodEnd,
      subscriptionSchedule,
    } = subscriptionInfo;
    this.planName = name;
    this.price = price;

    if (this.rootStore.featuresStore.has('BILLING_PAGE')) {
      this.cancelAtPeriodEnd = cancelAtPeriodEnd;
      this.subscriptionSchedule = subscriptionSchedule;
      this.updateBillingDates(currentPeriodStartAt, currentPeriodEndAt);
    }
  }

  @action.bound
  public reset() {
    this.planName = '';
    this.price = 0;
    this.amountDue = 0;
    this.paymentMethods = [];
    this.plansDetails = [];
    this.loadingPayments = false;
    this.cancelAtPeriodEnd = false;
    this.subscriptionSchedule = null;
    this.tenantInfo = undefined;
    this.billingTenure = undefined;
    this.firstLoad = false;
  }

  @action.bound
  public async updateDefaultBillingEmail(email: string) {
    const {
      organizationStore: { id: orgId },
      messagesStore,
    } = this.rootStore;
    try {
      await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).put(
        `organizations/${orgId}/billing/information`,
        {
          billingEmail: email,
        },
      );
      if (this.tenantInfo) {
        this.tenantInfo.invoiceEmails = [email];
      }
      messagesStore.showSuccessMessage('Successfully updated billing email');
    } catch (e) {
      messagesStore.showErrorMessage('Unable to update billing email');
    }
  }

  @action.bound
  public async getSelfServePlans() {
    const {
      organizationStore: { id: orgId },
      featuresStore,
      messagesStore,
    } = this.rootStore;
    try {
      if (featuresStore.has('BILLING_PAGE')) {
        const response = await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).get(
          `organizations/${orgId}/plans/selfServe`,
        );
        this.plansDetails = response.data.sort(
          (a: IPlanDetails, b: IPlanDetails) => a.eventVolume - b.eventVolume,
        );
      }
    } catch (e) {
      messagesStore.showErrorMessage('Unable get plans details');
    }
  }

  @action.bound
  public async getTenantDetail() {
    const { featuresStore, userStore } = this.rootStore;
    if ((!featuresStore.has('BILLING') && !featuresStore.has('USAGE')) || !userStore.isOrgAdmin) {
      this.firstLoad = true;
      return;
    }

    try {
      const resp = await apiAuthCaller({ region: Region.US })
        .ignore400()
        .get<ITenantInfo>('/tenant');
      this.tenantInfo = resp.data;
      if (featuresStore.has('USAGE_PAGE')) {
        this.updateBillingDates(billingCycleStartDate, billingCycleEndDate);
      }
    } catch (err) {
      // handle error
    } finally {
      this.firstLoad = true;
    }
  }

  private updateBillingDates(start: number, end: number) {
    this.billingTenure = { startDate: start * 1000, endDate: end * 1000 };
  }

  @action.bound
  public async redirectToBilling() {
    try {
      const resp: AxiosResponse<IBillingURL> = await apiAuthCaller({ region: Region.US }).get(
        `/billing/url?callbackUrl=${window.location.href}`,
      );
      window.location.href = resp.data.url;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to redirect to payment page');
      this.rootStore.messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async getPaymentMethods() {
    try {
      const {
        organizationStore: { id },
      } = this.rootStore;
      this.loadingPayments = true;
      const resp: AxiosResponse<IPaymentMethod[]> = await apiAuthCaller({
        nonWorkspaceRoute: true,
        region: Region.US,
      }).get(`organizations/${id}/paymentMethods`);
      this.loadingPayments = false;
      this.paymentMethods = resp.data;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to get Plans Details');
      this.rootStore.messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async updatePaymentMethod(id: string, cardDetails: ICardDetails, makeDefault?: boolean) {
    const {
      messagesStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    try {
      await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).patch(
        `organizations/${orgId}/paymentMethods/${id}`,
        cardDetails,
      );
      if (makeDefault) {
        messagesStore.showSuccessMessage(
          `Added card details successfully and made it as default payment method`,
        );
      } else {
        messagesStore.showSuccessMessage('Updated card details successfully');
      }
      await this.getTenantDetail();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to update card details');
      messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async makeDefaultPayment(isDefault: boolean) {
    const { messagesStore } = this.rootStore;
    try {
      await this.getPaymentMethods();
      if (isDefault || this.paymentMethods.length === 1) {
        const deafultPaymethod = this.paymentMethods[0];
        await this.updatePaymentMethod(deafultPaymethod.id, { setDefault: true }, true);
      }
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        `Failed to make ${
          this.paymentMethods[this.paymentMethods.length - 1].id
        } as default payment`,
      );
      messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async updateSubscription(stripePriceId: string) {
    const {
      messagesStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    try {
      const { data } = !this.isFreeTier
        ? await apiAuthCaller({
            nonWorkspaceRoute: true,
            region: Region.US,
          }).put<SubscriptionResponse>(`organizations/${orgId}/subscriptions`, {
            stripePriceId,
          })
        : await apiAuthCaller({
            nonWorkspaceRoute: true,
            region: Region.US,
          }).post<SubscriptionResponse>(`organizations/${orgId}/subscriptions`, {
            stripePriceId,
          });

      // Handle 3DS authentication if required
      if (data.paymentStatus === 'requires_action') {
        await this.handle3DSAuthentication(data);
        return;
      }

      await this.handleSubscriptionSuccess(data);
      messagesStore.showSuccessMessage('Updated subscription successfully');
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to update subscription');
      messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async cancelSubscription() {
    const {
      messagesStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    try {
      await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).put(
        `organizations/${orgId}/subscriptions/cancel`,
      );
      await this.getPlanName();
      await this.getTenantDetail();
      messagesStore.showSuccessMessage('Succefully cancelled subscription');
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to cancel subscription');
      messagesStore.showErrorMessage(message);
    }
  }

  @action.bound
  public async getInvoicePreview(stripePriceId: string) {
    const {
      messagesStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    try {
      const response = await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).get(
        `organizations/${orgId}/invoices/preview`,
        { params: { stripePriceId } },
      );
      if (response.data) {
        this.amountDue = response.data.amountDue;
      }
    } catch (error) {
      messagesStore.showErrorMessage('Unable to get amount due');
    }
  }

  @action.bound
  public async deletePaymentMethod(paymentMethod: IPaymentMethod) {
    const {
      messagesStore,
      organizationStore: { id },
    } = this.rootStore;
    try {
      await apiAuthCaller({ nonWorkspaceRoute: true, region: Region.US }).delete(
        `organizations/${id}/paymentMethods/${paymentMethod.id}`,
      );
      messagesStore.showSuccessMessage(
        `You’ve deleted ${paymentMethod.card.brand} ending in ${paymentMethod.card.last4}`,
      );
    } catch (error) {
      messagesStore.showErrorMessage(
        `Failed to delete the ${paymentMethod.card.brand} ending in ${paymentMethod.card.last4}`,
      );
    }
  }

  @computed get nextBillingDate() {
    if (this.billingTenure && this.billingTenure.endDate) {
      return this.billingTenure.endDate;
    }
    return undefined;
  }

  @computed get currentPlan() {
    const { planId } = this.tenantInfo || {};
    return this.plansDetails.find((p) => p.id === planId);
  }

  @computed get isFreeTier() {
    return this.tenantInfo?.planName === 'Free Tier' || false;
  }

  @action.bound
  private async handleSubscriptionSuccess(data: SubscriptionResponse) {
    const { featuresStore, organizationStore } = this.rootStore;

    if (data.planName && data.planId && this.tenantInfo) {
      this.tenantInfo.planId = data.planId;
      this.tenantInfo.planName = data.planName;
    } else {
      await this.getTenantDetail();
    }

    await featuresStore.getFeatures();
    await organizationStore.loadEventUsageStatus();
    await this.getPlanName();
  }

  @action.bound
  private async handle3DSAuthentication(data: SubscriptionResponse) {
    const { messagesStore } = this.rootStore;
    const { clientSecret, paymentMethod } = data;
    try {
      const stripe = await loadStripe(stripePublishableKey);

      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod,
      });

      if (result.error) {
        messagesStore.showErrorMessage(
          `${result.error.message}. You can retry the payment again from invoices page`,
        );
        return;
      }

      if (result.paymentIntent.status === 'succeeded') {
        await this.handleSubscriptionSuccess(data);
        messagesStore.showSuccessMessage('Payment confirmed and subscription updated successfully');
      }
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to authenticate payment');
      messagesStore.showErrorMessage(message);
    }
  }
}
